import { useEffect, useState } from "react";
import { SuccessFetch } from "types";
import { useApi } from "utils";

type BalanceSheetType = {
    assets: number,
    liabilities: number,
    equities: number,
    assets_percentage: number,
    liabilities_percentage: number,
    equities_percentage: number,
}

export function useFinancialPosition({ date, location_id }: { date: string, location_id: string | undefined}) {
    const api = useApi();

    const [data, setData] = useState<BalanceSheetType | null>(null);

    const params: Record<string, string | number> = {
        yearmonth: date,
    };

    if (location_id) {
        params.location_id = location_id;
    }

    const fetchUrl = () => {
        return api.get(`/dashboard/financial-position-and-cash/balance-sheet`, { params })
    }

    const fetchData = () => {
        fetchUrl().then((res: SuccessFetch<{
            payload: BalanceSheetType
        }>) => {
            setData(res.data.payload);
        }).catch(() => {
            setData(null);
        })
    }

    useEffect(() => {
        fetchData();
        return () => {
            setData(null);
        }
        // eslint-disable-next-line
    }, [date, location_id]);
    return { data }
}

type OveralCashBalance = {
    overall_cash_balance: number,
    coa_cash_balance: {
        coa_name: string,
        cash_balance: number
    }[]
}

export function useOveralCashBalance({ date, location_id }: { date: string, location_id: string | undefined}) {
    const api = useApi();

    const [data, setData] = useState<OveralCashBalance | null>(null);

    const params: Record<string, string | number> = {
        yearmonth: date,
    };

    if (location_id) {
        params.location_id = location_id;
    }

    const fetchUrl = () => {
        return api.get(`/dashboard/financial-position-and-cash/overall-cash-balance`, { params })
    }

    const fetchData = () => {
        fetchUrl().then((res: SuccessFetch<{
            payload: OveralCashBalance
        }>) => {
            setData(res.data.payload);
        }).catch(() => {
            setData(null);
        })
    }

    useEffect(() => {
        fetchData();
        return () => {
            setData(null);
        }
        // eslint-disable-next-line
    }, [date, location_id]);
    return { data }
}


type LargeExpansese = {
    coa_id: number,
    coa_name: string,
    balance_before: number,
    current_balance: number
}

export function useLargeExpansese({ date, location_id }: { date: string, location_id: string | undefined}) {
    const api = useApi();

    const [data, setData] = useState<LargeExpansese[]>([]);
    const [loading, setLoading] = useState(false);

    const params: Record<string, string | number> = {
        year_month: date,
    };

    if (location_id) {
        params.location_id = location_id;
    }

    const fetchUrl = () => {
        return api.get(`/dashboard_largest/get_largest_expense/`, { params })
    }

    const fetchData = () => {
        setLoading(true);
        fetchUrl().then((res: SuccessFetch<{
            payload: LargeExpansese[]
        }>) => {
            setData(res.data.payload);
        }).catch(() => {
            setData([]);
        }).finally(() => {
            setLoading(false);
        })
    }

    useEffect(() => {
        fetchData();
        return () => {
            setData([]);
            setLoading(false);
        }
        // eslint-disable-next-line
    }, [date, location_id]);
    return { data, loading }
}


type AverageTime = {
    total_ar: number,
    total_credit_sales: number,
    number_of_days: number,
    dso: number,
    value_of_all_invoice:number
}

export function useAverageTime({ date, location_id }: { date: string, location_id: string | undefined}) {
    const api = useApi();

    const [data, setData] = useState<AverageTime | null>(null);
    const [loading, setLoading] = useState(false);

    const params: Record<string, string | number> = {
        yearmonth: date,
    };

    if (location_id) {
        params.location_id = location_id;
    }

    const fetchUrl = () => {
        return api.get(`/dashboard/financial-position-and-cash/average-time-to-get-paid`, { params })
    }

    const fetchData = () => {
        setLoading(true);
        fetchUrl().then((res: SuccessFetch<{
            payload: AverageTime
        }>) => {
            setData(res.data.payload);
        }).catch(() => {
            setData(null);
        }).finally(() => {
            setLoading(false);
        })
    }

    useEffect(() => {
        fetchData();
        return () => {
            setData(null);
            setLoading(false);
        }
        // eslint-disable-next-line
    }, [date, location_id]);
    return { data, loading }
}

type AveragePaySupplier = {
    total_ap: number,
    cogs: number,
    number_of_days: number,
    dpo: number,
    unpaid_bill?:number,
    value_of_all_bill:number
}


export function useAveragePaySupplier({ date, location_id }: { date: string, location_id: string | undefined}) {
    const api = useApi();

    const [data, setData] = useState<AveragePaySupplier | null>(null);
    const [loading, setLoading] = useState(false);

    const params: Record<string, string | number> = {
        yearmonth: date,
    };

    if (location_id) {
        params.location_id = location_id;
    }

    const fetchUrl = () => {
        return api.get(`/dashboard/financial-position-and-cash/average-time-to-get-pay-supplier`, { params })
    }

    const fetchData = () => {
        setLoading(true);
        fetchUrl().then((res: SuccessFetch<{
            payload: AveragePaySupplier
        }>) => {
            setData(res.data.payload);
        }).catch(() => {
            setData(null);
        }).finally(() => {
            setLoading(false);
        })
    }

    useEffect(() => {
        fetchData();
        return () => {
            setData(null);
            setLoading(false);
        }
        // eslint-disable-next-line
    }, [date, location_id]);
    return { data, loading }
}

